/* Analýza V2+ — smlouvy, které klient MÁ před analýzou, do klientské složky.
 *
 * PROČ SAMOSTATNÝ MODUL:
 * Logika žila uvnitř syncSmlouvy() v AnalyzaV2Page.js, kde se nedala otestovat
 * bez axiosu a celé stránky. Přitom je to nejcitlivější místo celého propsání:
 * když se splete dedup, buď se dvě různé pojistky slijí v jednu, nebo se tatáž
 * pojistka založí znovu při každém uložení analýzy.
 *
 * NÁLEZ VLASTNÍKA (7. 8. 2026):
 * Když poradce nevyplnil číslo smlouvy, uložil se jako číslo smlouvy POPISEK
 * ŘÁDKU z analýzy — např. Pojištění auta. Ve složce pak seděla smlouva
 * s číslem, které klient nikdy neviděl. Doslova: „objeví se smlouva pojištění
 * auta — ale není to jeho smlouva."
 *
 * PRAVIDLO MODULU:
 * `contract_number` nese VÝHRADNĚ číslo, které poradce opsal ze smlouvy.
 * Když ho nemá, zůstane prázdné a složka napíše, že číslo není vyplněné.
 * Popisek řádku z analýzy se ukládá zvlášť (details.analyza_oznaceni), aby
 * se informace neztratila — ale nikdy se nevydává za číslo smlouvy.
 *
 * DEDUP (tři klíče, v tomhle pořadí):
 *   1. details.analyza_klic — `zdroj:id řádku` analýzy. Stabilní přes uložení,
 *      nezávislý na tom, co poradce vyplnil. Hlavní pojistka proti zdvojení.
 *   2. typ + instituce + ČÍSLO — jen když poradce číslo opravdu vyplnil.
 *      Chytí smlouvu, kterou poradce zapsal do složky ručně.
 *   3. typ + instituce + POPISEK řádku — tvar, který zapisovala starší verze
 *      (popisek v poli contract_number). Bez něj by se po nasazení opravy
 *      všechny dřív propsané smlouvy založily podruhé.
 *
 * Prázdné číslo se NIKDY nepoužije jako samostatný klíč. Kdyby ano, dvě různé
 * pojistky u téže pojišťovny bez čísla (nemovitost + odpovědnost) by měly
 * stejný klíč a druhá by se do složky nedostala vůbec.
 *
 * Čisté funkce, žádný React ani síť.
 */

const txt = (v) => String(v ?? "").trim();
const num = (v) => (Number.isFinite(+v) ? +v : 0);
const arr = (v) => (Array.isArray(v) ? v : []);

/** Poznámka u smlouvy přenesené z analýzy — složka podle ní kreslí štítek. */
export const POZNAMKA_Z_ANALYZY = "Z analýzy — smlouva před analýzou";

/** Dedup klíč staré i ruční smlouvy: typ + instituce + text (číslo nebo popisek). */
export const klicSmlouvy = (typ, instituce, text) =>
  `${txt(typ)}|${txt(instituce).toLowerCase()}|${txt(text).toLowerCase()}`;

/* ── TYP POJISTNÉ SMLOUVY VE SLOŽCE ────────────────────────────────────────
 * Kontrola 4. 10. 2026: typ se dřív HÁDAL jen z popisku řádku a „odpov“
 * padalo do majetku — pojištění odpovědnosti (pj4) se ve složce ukázalo jako
 * „Pojištění majetku“ (a výpověď by šla na vzor „adresa nemovitosti“).
 * Právní ochrana, cestovní či podnikatelské pojištění zase spadly do
 * životního — a krizový plán čte pojisteni_zivot jako krytí života.
 *
 * Rozhoduje DRUH řádku (číselník DRUHY[POJISTNE] v zavazkyTaby.js), popisek
 * jen tam, kde druh chybí (starší data, vlastní řádky).
 *
 * VLASTNÍ TYPY OD 4. 10. 2026 (majitel, bod 16: „Pojištění odpovědnosti jako
 * vlastní druh smlouvy ve složce + vzor výpovědi“). Do té doby CRM (backend
 * models_contracts.ContractType) znalo jen život / auto / majetek a obecné
 * „jine“ — bez tlačítka Výpověď a bez hlídání výpovědního okna. Teď:
 *   pojisteni_odpovednost — odpovědnost, zaměstnavatelské (= odpovědnost
 *                           zaměstnance za škodu způsobenou zaměstnavateli),
 *   pojisteni_ostatni     — právní ochrana, cestovní, podnikatelské.
 * Skutečný druh dál nese details.druh_pojisteni (backend z něj píše druh do
 * výpovědi) a popisek details.analyza_oznaceni.
 *
 * Týká se jen NOVÝCH propisů: u už propsané smlouvy sync typ nepřepisuje
 * (viz SYNC_POLE_SMLOUVY — type v něm schválně není), takže smlouvy založené
 * dřív jako „jine“ zůstanou „jine“, dokud je poradce nepřepne sám. */
export const DRUH_POJISTKY_NA_TYP = {
  zivot: "pojisteni_zivot",
  uraz: "pojisteni_zivot",
  nemovitost: "pojisteni_majetek",
  domacnost: "pojisteni_majetek",
  povinne_ruceni: "pojisteni_auta",
  havarijni: "pojisteni_auta",
  odpovednost: "pojisteni_odpovednost",
  zamestnavatelske: "pojisteni_odpovednost",
  pravni_ochrana: "pojisteni_ostatni",
  cestovni: "pojisteni_ostatni",
  podnikatelske: "pojisteni_ostatni",
};

/* Typy, které přibyly 4. 10. 2026. Backend nasazený PŘED touhle změnou je
 * nezná (422) — viz payloadProStaryBackend / zalozSmlouvu níž. */
export const NOVE_TYPY_POJISTEK = Object.freeze(["pojisteni_odpovednost", "pojisteni_ostatni"]);

/* Typ pojistné smlouvy — určuje i to, jestli složka nabídne tlačítko Výpověď
 * do pojišťovny (lib/cancellationLetter.js → isInsuranceType). */
export function typPojistky(naz, druh) {
  const d = txt(druh);
  if (d && Object.prototype.hasOwnProperty.call(DRUH_POJISTKY_NA_TYP, d)) return DRUH_POJISTKY_NA_TYP[d];
  const s = txt(naz);
  if (/majet|nemovit|dom[aá]cnost/i.test(s)) return "pojisteni_majetek";
  if (/auto|povinn|havarij|vozid/i.test(s)) return "pojisteni_auta";
  if (/odpov|zam[eě]stnavatel/i.test(s)) return "pojisteni_odpovednost";
  if (/pr[aá]vn|cestovn|podnikatel/i.test(s)) return "pojisteni_ostatni";
  return "pojisteni_zivot";
}

/* Typ, který STARŠÍ verze zapsala (jen podle popisku, odpovědnost = majetek).
 * Potřebuje ho jen dedup: smlouva propsaná dřív bez details nese starý typ
 * a jeho klíč — bez tohohle by se po změně typu založila podruhé. */
export function typPojistkyDrive(naz) {
  const s = txt(naz);
  if (/majet|nemovit|domacnost|odpov/i.test(s)) return "pojisteni_majetek";
  if (/auto|povinn|havarij|vozid/i.test(s)) return "pojisteni_auta";
  return "pojisteni_zivot";
}

/* Všechny typy, pod kterými mohla tatáž pojistka ve složce už ležet:
 *   • typ podle popisku ze starší verze (odpovědnost = majetek),
 *   • „jine“ — tak se odpovědnost, právní ochrana… propisovaly do 4. 10. 2026
 *     a tak je poradce zapisoval ručně, když vlastní typ chyběl.
 * Dedup podle nich pozná smlouvu i tehdy, když jí chybí details.analyza_klic. */
export function typyPojistkyDrive(naz, typ) {
  const out = [typPojistkyDrive(naz)];
  if (NOVE_TYPY_POJISTEK.includes(typ)) out.push("jine");
  return [...new Set(out)].filter((t) => t !== typ);
}

/* ── ZPĚTNÁ KOMPATIBILITA S BACKENDEM PŘED NASAZENÍM ──────────────────────
 * Frontend a backend se nasazují zvlášť (backend až po redeployi na Emergentu).
 * Starý backend nový typ odmítne 422 — a propis na pozadí by to zkoušel každých
 * ~12 s donekonečna, pojistka by se do složky nedostala nikdy. Proto se při 422
 * u NOVÉHO typu smlouva založí jednou znovu jako „jine“ (jako do 4. 10. 2026);
 * druh i popisek v details zůstanou, takže poradce ji ve složce pozná. */
export function payloadProStaryBackend(payload) {
  if (!payload || !NOVE_TYPY_POJISTEK.includes(payload.type)) return null;
  return { ...payload, type: "jine" };
}

/**
 * Založí smlouvu ve složce; na starém backendu (422 u nového typu) zkusí
 * jednou znovu jako „jine“. Jakoukoli jinou chybu nechá probublat — volající
 * podle ní pozná, že složka srovnaná NENÍ.
 *
 * @param post  (body) => Promise — typicky api.post("/api/contracts", body)
 * @param body  { contact_id, ...payload }
 */
export async function zalozSmlouvu(post, body) {
  try {
    return await post(body);
  } catch (e) {
    const zaloha = e?.response?.status === 422 ? payloadProStaryBackend(body) : null;
    if (!zaloha) throw e;
    return post(zaloha);
  }
}

/* Hypotéka vs. spotřebitelský úvěr — podle popisku řádku závazku. */
export const jeHypoteka = (naz) => /hypot|bydlen/i.test(txt(naz));

/** Má pojistka z analýzy co dělat ve složce? Jen s pojišťovnou nebo platbou (bod 15). */
export function pojistkaDoSlozky(r) {
  return num(r?.platba) > 0 || txt(r?.instituce) !== "";
}

/**
 * Kandidáti na smlouvu ve složce ze zadané analýzy (data.v2).
 *
 * Vrací pole `{ analyzaKlic, cisloKlic, legacyKlic, payload }`. Sestavení
 * a rozhodnutí, co je nové, jsou schválně dva kroky — `noveSmlouvy()` se pak
 * dá otestovat proti libovolnému stavu složky.
 */
export function smlouvyProSlozku(d = {}) {
  const m = d?.majetek || {};
  const out = [];

  /* `drivejsiTypy` = typy, pod kterými tutéž smlouvu propisovala starší verze
   * nebo ji poradce zapsal ručně (jen pojistky, a jen když se liší). Dedup pak
   * pozná i takový zápis. */
  const push = (zdroj, r, payload, { drivejsiTypy = [], detailsNavic = null } = {}) => {
    const oznaceni = txt(r?.oznaceni);
    const cislo = txt(payload.contract_number);
    const analyzaKlic = `${zdroj}:${txt(r?.id) || oznaceni}`;
    const drivejsiKlice = [];
    for (const drivejsiTyp of drivejsiTypy) {
      if (!drivejsiTyp || drivejsiTyp === payload.type) continue;
      if (cislo) drivejsiKlice.push(klicSmlouvy(drivejsiTyp, payload.provider, cislo));
      if (cislo || oznaceni) drivejsiKlice.push(klicSmlouvy(drivejsiTyp, payload.provider, cislo || oznaceni));
    }
    out.push({
      analyzaKlic,
      cisloKlic: cislo ? klicSmlouvy(payload.type, payload.provider, cislo) : "",
      legacyKlic: cislo || oznaceni ? klicSmlouvy(payload.type, payload.provider, cislo || oznaceni) : "",
      drivejsiKlice,
      payload: {
        ...payload,
        status: "active",
        notes: POZNAMKA_Z_ANALYZY,
        // Popisek řádku patří sem, ne do čísla smlouvy. Zároveň je to záložní
        // dedup klíč, kdyby se details cestou ztratilo.
        details: { analyza_klic: analyzaKlic, analyza_oznaceni: oznaceni, ...(detailsNavic || {}) },
      },
    });
  };

  /* Produkty (stavebko, penzijko, OPF/ETF, KŽP) žádné pole pro číslo smlouvy
   * nemají — proto contract_number zůstává prázdné vždy. */
  const produkt = (kat, type) =>
    arr(m[kat]).forEach((r) => {
      if (!(num(r?.hodnota) > 0 || num(r?.vklad) > 0)) return;
      push(kat, r, {
        type,
        provider: txt(r?.instituce),
        contract_number: "",
        total_amount: num(r?.hodnota),
        monthly_amount: num(r?.vklad),
      });
    });
  produkt("stavebko", "stavebni");
  produkt("penzijko", "penzijko");
  produkt("opf", "investice");
  produkt("kzp", "pojisteni_zivot");

  arr(d?.zavazkyB).forEach((r) => {
    if (!(num(r?.jistina) > 0 || num(r?.splatka) > 0)) return;
    push("zavazkyB", r, {
      type: jeHypoteka(r?.oznaceni) ? "hypoteka" : "uver",
      provider: txt(r?.instituce),
      contract_number: "",
      total_amount: num(r?.jistina),
      monthly_amount: num(r?.splatka),
      rate: num(r?.urok),
      fixation_end: r?.fixace || null,
    });
  });

  arr(d?.zavazkyO).forEach((r) => {
    if (!(num(r?.jistina) > 0 || num(r?.splatka) > 0)) return;
    push("zavazkyO", r, {
      type: "uver",
      provider: txt(r?.instituce),
      contract_number: "",
      total_amount: num(r?.jistina),
      monthly_amount: num(r?.splatka),
      rate: num(r?.urok),
      anniversary_date: r?.vyroci || null,
    });
  });

  /* Pojistky jsou JEDINÁ kategorie, kde poradce číslo smlouvy opravdu opisuje.
   *
   * CO SE PROPÍŠE (majitel 4. 10. 2026, bod 15): „Do složky klienta propisovat
   * jen pojistky s pojišťovnou nebo platbou (ne prázdné řádky).“ Dřív stačilo
   * samotné číslo smlouvy — a ve složce pak seděla „smlouva“ bez pojišťovny
   * i bez pojistného, kterou nešlo ani vypovědět (komu?), ani započítat.
   * Číslo, výročí či poznámka bez pojišťovny a platby řádek do složky
   * NEPUSTÍ; jakmile poradce doplní pojišťovnu nebo platbu, propíše se celý
   * i s číslem. (Stejné pravidlo drží kdyPropsat.js, ať stavová lišta neříká
   * „srovnáno“ u řádku, který ve složce není.) */
  arr(d?.pojistky).forEach((r) => {
    if (!pojistkaDoSlozky(r)) return;
    const druh = txt(r?.druh);
    const type = typPojistky(r?.oznaceni, druh);
    push("pojistky", r, {
      type,
      provider: txt(r?.instituce),
      contract_number: txt(r?.cislo),
      monthly_amount: num(r?.platba),
      anniversary_date: r?.vyroci || null,
    }, {
      drivejsiTypy: typyPojistkyDrive(r?.oznaceni, type),
      detailsNavic: druh ? { druh_pojisteni: druh } : null,
    });
  });

  return out;
}

/* Popisek řádku analýzy z už uložené smlouvy. Nové zápisy ho mají v details,
 * starší (před opravou) ho měly natvrdo v contract_number. */
function oznaceniUlozene(c) {
  const zDetailu = txt(c?.details?.analyza_oznaceni);
  if (zDetailu) return zDetailu;
  // Starší zápis: popisek se ukládal jako číslo smlouvy. Poznáme ho podle
  // poznámky z analýzy — u ručně zapsané smlouvy je contract_number opravdové.
  if (/z anal[ýy]zy/i.test(txt(c?.notes))) return txt(c?.contract_number);
  return "";
}

/** Dedup klíče, kterými se dá poznat smlouva, co už ve složce je. */
export function kliceExistujici(c = {}) {
  const oznaceni = oznaceniUlozene(c);
  const klice = [klicSmlouvy(c?.type, c?.provider, c?.contract_number)];
  if (oznaceni) klice.push(klicSmlouvy(c?.type, c?.provider, oznaceni));
  return { analyzaKlic: txt(c?.details?.analyza_klic), klice };
}

/* ══════════════════════════════════════════════════════════════════════
 * AKTUALIZACE UŽ PROPSANÉ SMLOUVY (nález 9. 8. 2026)
 *
 * Sync uměl jen ZAKLÁDAT. Když poradce v analýze opravil překlep ve splátce
 * hypotéky (18 000 → 21 500) a uložil, toast řekl „Analýza uložena", ale
 * smlouva ve složce zůstala navždy na 18 000 — kandidát se přeskočil, jakmile
 * jeho analyza_klic ve složce byl. U majetku se přitom tatáž oprava propsala
 * správně (zmenyMajetku), takže poradce měl důvod věřit, že to platí i tady.
 *
 * ANALÝZA JE VLASTNÍKEM SVÝCH ŘÁDKŮ — ale jen některých polí:
 *   ANO: instituce, jistina, splátka, úrok, fixace, výročí. Tohle poradce
 *        zadává v analýze a nikde jinde.
 *   NE:  `type` — odvozuje se HÁDÁNÍM z popisku řádku (typPojistky). Když si
 *        poradce ve složce opraví špatně uhodnutý typ, sync mu to nesmí
 *        každým uložením přepsat zpátky.
 *   NE:  `notes` a `details` — nese je štítek „z analýzy" a dedup klíč;
 *        přepisovat je nemá co přidat a poznámku poradce by to smazalo.
 *   JEN DOPLNIT: `contract_number` — číslo, které poradce ručně opsal do
 *        složky, je to nejdůvěryhodnější, co v systému je. Když ve složce
 *        číslo je, analýza ho nepřepisuje; když chybí, doplní ho.
 *
 * PÁRUJE SE VÝHRADNĚ PŘES `details.analyza_klic`. Smlouva rozpoznaná jen
 * legacy klíčem (starší zápis bez details) se nepatchuje — nevíme jistě, že
 * patří tomuhle řádku analýzy, a přepsat cizí smlouvu je horší než ji nechat.
 *
 * CO SYNC SCHVÁLNĚ NEDĚLÁ: nemaže. Řádek smazaný z analýzy nechá smlouvu
 * ve složce být — smaže ji poradce sám.
 * ══════════════════════════════════════════════════════════════════════ */

/** Pole, která analýza opravdu vlastní — jen ta se u existující smlouvy přepisují. */
export const SYNC_POLE_SMLOUVY = [
  "provider", "total_amount", "monthly_amount", "rate", "fixation_end", "anniversary_date",
];

/* Rovnost hodnoty pole. `undefined` z Mongo dokumentu (pole tam prostě není)
 * a `null` z payloadu znamenají totéž: nevyplněno. Bez tohohle by se PATCH
 * posílal při každém uložení donekonečna. */
const stejne = (a, b) => (a ?? null) === (b ?? null);

/* ── NEZNÁMÉ SE NEPROPISUJE (nález 9. 8. 2026, nezávislá kontrola) ──────────
 * Payload staví hodnoty přes txt()/num(), takže nevyplněné pole analýzy vyjde
 * jako "" nebo 0 — ne jako null. V patchi to znamenalo TICHOU ZTRÁTU DAT:
 * poradce zapsal hypotéku v analýze jen jako jistina + splátka, ve složce si
 * ručně doplnil „Česká spořitelna“ a 4,9 % — a při nejbližším uložení analýzy
 * odešel PATCH s provider="" a rate=0. Backend má na PATCHi exclude_none,
 * takže None zahodí, ale "" a 0 uloží. Ve složce pak u úroku svítilo „—“
 * a toast přitom hlásil „Smlouvy ve složce aktualizovány (1)“.
 *
 * Pravidlo: analýza je vlastníkem svých polí, ale VLASTNÍ JEN TO, CO VYPLNILA.
 * Prázdno v analýze znamená „nevím“, ne „smaž, co víš ve složce“ — stejné
 * pravidlo už platilo pro contract_number.
 *
 * Nula = nevyplněno schválně: model analýzy (lib/analyzaV2/model.js) má
 * u částek i úroku výchozí hodnotu 0, ne prázdný řetězec. Rozlišit „úrok 0 %“
 * od „úrok nevyplněný“ z těch dat NEJDE — a přepsat poradcem zapsaných 4,9 %
 * nulou je horší chyba než nepropsat skutečný bezúročný úvěr od rodiny.
 *
 * Vedlejší efekt: mizí i donekonečna opakovaný PATCH s null (např. fixace ve
 * složce vyplněná, v analýze ne) — ten se dřív posílal při každém uložení,
 * backend ho zahodil a nic se nestalo, ale síť i toast tvrdily opak. */
const analyzaVyplnila = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "number") return Number.isFinite(v) && v > 0;
  return txt(v) !== "";
};

/**
 * Co je potřeba ve složce udělat, aby smlouvy odpovídaly analýze.
 *
 * @param d           data.v2 analýzy
 * @param existujici  co vrátí GET /api/contracts?contact_id=…
 * @returns { nove: [payload], zmenene: [{ id, patch }] }
 *
 * Opakované uložení nezměněné analýzy vrátí { nove: [], zmenene: [] }.
 */
export function zmenySmluv(d = {}, existujici = []) {
  const videneAnalyzy = new Set();
  const videneKlice = new Set();
  const podleAnalyzy = new Map();
  for (const c of arr(existujici)) {
    const k = kliceExistujici(c);
    if (k.analyzaKlic) {
      videneAnalyzy.add(k.analyzaKlic);
      // Kdyby ve složce omylem seděly dvě smlouvy s týmž klíčem, bereme první —
      // druhou raději necháme být, než abychom ji tiše přepsali.
      if (!podleAnalyzy.has(k.analyzaKlic)) podleAnalyzy.set(k.analyzaKlic, c);
    }
    for (const kl of k.klice) videneKlice.add(kl);
  }

  const nove = [];
  const zmenene = [];
  const zpracovane = new Set();
  for (const kand of smlouvyProSlozku(d)) {
    // Pojistka proti duplicitě uvnitř jedné dávky.
    if (zpracovane.has(kand.analyzaKlic)) continue;
    zpracovane.add(kand.analyzaKlic);

    const uz = podleAnalyzy.get(kand.analyzaKlic);
    if (uz) {
      if (!txt(uz.id)) continue;   // bez id není co PATCHovat
      const patch = {};
      for (const pole of SYNC_POLE_SMLOUVY) {
        if (!(pole in kand.payload)) continue;   // kategorie to pole vůbec nenese
        const nova = kand.payload[pole];
        if (!analyzaVyplnila(nova)) continue;    // prázdno v analýze = „nevím“, ne „smaž“
        if (!stejne(uz[pole], nova)) patch[pole] = nova;
      }
      const cislo = txt(kand.payload.contract_number);
      if (cislo && !txt(uz.contract_number)) patch.contract_number = cislo;
      if (Object.keys(patch).length) zmenene.push({ id: txt(uz.id), patch });
      continue;
    }

    if (videneAnalyzy.has(kand.analyzaKlic)) continue;
    if (kand.cisloKlic && videneKlice.has(kand.cisloKlic)) continue;
    if (kand.legacyKlic && videneKlice.has(kand.legacyKlic)) continue;
    // Smlouva propsaná dřív pod STARÝM typem (odpovědnost = majetek) bez details.
    if ((kand.drivejsiKlice || []).some((k) => videneKlice.has(k))) continue;
    videneAnalyzy.add(kand.analyzaKlic);
    // Do klíčů přidáváme jen SKUTEČNÉ číslo a popisek — nikdy prázdno, jinak
    // by druhá pojistka téže pojišťovny bez čísla propadla jako duplicita.
    if (kand.cisloKlic) videneKlice.add(kand.cisloKlic);
    if (kand.legacyKlic) videneKlice.add(kand.legacyKlic);
    nove.push(kand.payload);
  }
  return { nove, zmenene };
}

/**
 * Payloady smluv, které ve složce ještě NEJSOU.
 *
 * Idempotence: opakované uložení téže analýzy vrátí prázdné pole.
 */
export function noveSmlouvy(d = {}, existujici = []) {
  return zmenySmluv(d, existujici).nove;
}
